/*
  <header-v2> — behaviour for sections/header-v2.liquid.
  Desktop: hover/click/keyboard mega panels. Mobile: two-level drawer.
  Also: transparent → filled on scroll (mobile), --header-height, utility strip
  placement next to the announcement bar, cart count mirroring and the showroom
  live status.

  Motion follows the huts.com dropdown: the panels share one container whose
  height animates (open from 0, resize on switch, collapse on close) while the
  panel contents crossfade; the backdrop stays up while switching.
*/
(() => {
  if (customElements.get('header-v2')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 200;
  const MENU_MS = 400; // container height / drawer reveal (--hv2-menu-ms)
  const FADE_MS = 200; // panel content fade (--hv2-fade-ms)
  const LEVEL_EXIT_MS = 260;
  const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class HeaderV2 extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.backdrop = this.querySelector('[data-hv2-backdrop]');
      this.drawer = this.querySelector('[data-hv2-drawer]');
      this.drawerToggle = this.querySelector('[data-hv2-drawer-toggle]');
      this.mobileBar = this.querySelector('.hv2-row.hv2-mobile');
      this.openHandle = null;
      this.openTimer = null;
      this.closeTimer = null;

      this.initPanels();
      this.initDrawer();
      this.initShowroomStatus();
      this.initTransparent();
      this.initHeight();
      this.initUtilityStrip();
      this.initCartCount();

      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onBreakpoint = () => this.closeAll(false);

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pageshow', this.onPageShow);
      this.desktop.addEventListener('change', this.onBreakpoint);
      this.backdrop?.addEventListener('click', () => this.closeAll(false));
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('scroll', this.onScroll);
      this.desktop.removeEventListener('change', this.onBreakpoint);
      this.resizeObserver?.disconnect();
      this.panelObserver?.disconnect();
      this.cartObserver?.disconnect();
      document.documentElement.classList.remove('hv2-locked', 'hv2-panel-locked');
    }

    /* ---------- Desktop panels ---------- */

    initPanels() {
      this.triggers = [...this.querySelectorAll('[data-hv2-trigger]')];
      this.panelsEl = this.querySelector('.hv2-panels');

      // Keep the container height in step with the open panel (lazy images, resize).
      if ('ResizeObserver' in window && this.panelsEl) {
        this.panelObserver = new ResizeObserver(() => {
          if (this.openHandle) this.setPanelsHeight(this.getPanel(this.openHandle).offsetHeight);
        });
      }

      this.triggers.forEach((trigger) => {
        const handle = trigger.dataset.hv2Trigger;
        const panel = this.getPanel(handle);
        if (!panel) return;

        trigger.addEventListener('mouseenter', () => {
          this.cancelClose();
          clearTimeout(this.openTimer);
          // Switching between items while a panel is open should feel instant.
          const delay = this.openHandle ? 0 : OPEN_DELAY;
          this.openTimer = setTimeout(() => this.openPanel(handle, { via: 'hover' }), delay);
        });
        trigger.addEventListener('mouseleave', () => {
          clearTimeout(this.openTimer);
          this.scheduleClose();
        });
        trigger.addEventListener('click', () => {
          const isOpen = this.openHandle === handle;
          // A hover-open immediately followed by a click should keep the panel open.
          if (isOpen && Date.now() - this.openedAt > 400) {
            this.closePanel();
          } else {
            this.openPanel(handle, { via: 'click' });
          }
        });

        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleClose());
      });

      // Hovering a plain link (Sale, etc.) closes any open panel.
      this.querySelectorAll('.hv2-nav a.hv2-nav__link').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          clearTimeout(this.openTimer);
          if (this.openHandle) this.closePanel();
        });
      });
    }

    getPanel(handle) {
      return this.querySelector(`[data-hv2-panel="${CSS.escape(handle)}"]`);
    }

    getTrigger(handle) {
      return this.querySelector(`[data-hv2-trigger="${CSS.escape(handle)}"]`);
    }

    openPanel(handle) {
      if (!this.desktop.matches) return;
      this.cancelClose();
      if (this.openHandle === handle) return;

      const panel = this.getPanel(handle);
      const trigger = this.getTrigger(handle);
      if (!panel || !trigger) return;

      // Switching: fade the outgoing panel out but leave the container and
      // backdrop up, so there is no close → reopen flash.
      const previous = this.openHandle;
      if (previous) this.hidePanel(previous, FADE_MS);

      clearTimeout(panel.hv2HideTimer);
      panel.hidden = false;
      if (!previous) {
        // Lock the page so wheel/trackpad over the panel or backdrop doesn't scroll it.
        this.style.setProperty('--hv2-panels-top', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
        this.panelsEl.scrollTop = 0;
        document.documentElement.classList.add('hv2-panel-locked');
      }
      this.setPanelsHeight(panel.offsetHeight);
      requestAnimationFrame(() => panel.classList.add('is-open'));

      trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-panel-open');
      this.openHandle = handle;
      this.openedAt = Date.now();
      this.panelObserver?.disconnect();
      this.panelObserver?.observe(panel);
      this.showBackdrop();
    }

    closePanel({ immediate = false, returnFocus = false } = {}) {
      const handle = this.openHandle;
      if (!handle) return;
      const trigger = this.getTrigger(handle);

      this.openHandle = null;
      this.panelObserver?.disconnect();
      this.classList.remove('is-panel-open');
      document.documentElement.classList.remove('hv2-panel-locked');
      // Contents fade while the container collapses; unmount once it has.
      this.hidePanel(handle, immediate ? 0 : MENU_MS);
      this.setPanelsHeight(0, immediate);

      if (!this.classList.contains('is-drawer-open')) this.hideBackdrop(immediate);
      if (returnFocus) trigger?.focus();
    }

    hidePanel(handle, delay) {
      const panel = this.getPanel(handle);
      this.getTrigger(handle)?.setAttribute('aria-expanded', 'false');
      if (!panel) return;
      panel.classList.remove('is-open');
      clearTimeout(panel.hv2HideTimer);
      if (!delay) {
        panel.hidden = true;
        return;
      }
      panel.hv2HideTimer = setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, delay);
    }

    setPanelsHeight(height, immediate = false) {
      if (!this.panelsEl) return;
      if (immediate) {
        this.panelsEl.style.transition = 'none';
        this.panelsEl.style.height = `${height}px`;
        void this.panelsEl.offsetHeight;
        this.panelsEl.style.transition = '';
        return;
      }
      this.panelsEl.style.height = `${height}px`;
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.closePanel(), CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    showBackdrop() {
      if (!this.backdrop) return;
      clearTimeout(this.backdropTimer);
      this.backdrop.hidden = false;
      requestAnimationFrame(() => this.backdrop.classList.add('is-visible'));
    }

    hideBackdrop(immediate = false) {
      if (!this.backdrop) return;
      this.backdrop.classList.remove('is-visible');
      clearTimeout(this.backdropTimer);
      if (immediate) {
        this.backdrop.hidden = true;
        return;
      }
      this.backdropTimer = setTimeout(() => {
        if (!this.backdrop.classList.contains('is-visible')) this.backdrop.hidden = true;
      }, MENU_MS);
    }

    // The backdrop starts under the header, so clicks on the announcement/utility
    // bar or the bar's empty space need their own close (a click-opened panel
    // has no mouseleave to close it, e.g. on touch).
    onPointerDown(event) {
      if (!this.openHandle) return;
      if (this.panelsEl?.contains(event.target) || event.target.closest?.('[data-hv2-trigger]')) return;
      this.closePanel();
    }

    onFocusIn(event) {
      if (!this.openHandle) return;
      const panel = this.getPanel(this.openHandle);
      const trigger = this.getTrigger(this.openHandle);
      if (panel?.contains(event.target) || trigger === event.target) return;
      this.closePanel();
    }

    /* ---------- Mobile drawer ---------- */

    initDrawer() {
      if (!this.drawer || !this.drawerToggle) return;
      this.rootLevel = this.drawer.querySelector('[data-hv2-level="root"]');

      this.drawerToggle.addEventListener('click', () => {
        if (this.classList.contains('is-drawer-open')) this.closeDrawer();
        else this.openDrawer();
      });

      this.drawer.querySelectorAll('[data-hv2-open-level]').forEach((button) => {
        button.addEventListener('click', () => this.openLevel(button.dataset.hv2OpenLevel, button));
      });

      this.drawer.querySelectorAll('[data-hv2-back]').forEach((button) => {
        button.addEventListener('click', () => this.closeLevel());
      });
    }

    openDrawer() {
      const barBottom = this.mobileBar.getBoundingClientRect().bottom;
      this.style.setProperty('--hv2-drawer-top', `${Math.max(0, barBottom)}px`);
      // Reopened mid-close: the pending reset never ran, so start from the root.
      if (this.drawerTimer) {
        clearTimeout(this.drawerTimer);
        this.drawerTimer = null;
        this.resetLevels();
      }
      this.drawer.hidden = false;
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      this.classList.add('is-drawer-open');
      this.drawerToggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('hv2-locked');
      this.rootLevel.scrollTop = 0;
      this.rootLevel.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    }

    closeDrawer(returnFocus = true, immediate = false) {
      if (!this.classList.contains('is-drawer-open')) return;
      this.drawer.classList.remove('is-open');
      clearTimeout(this.drawerTimer);
      // Levels reset once the drawer has slid shut, so the view doesn't jump mid-close.
      const finish = () => {
        this.drawerTimer = null;
        if (this.drawer.classList.contains('is-open')) return;
        this.resetLevels();
        this.drawer.hidden = true;
      };
      if (immediate) finish();
      else this.drawerTimer = setTimeout(finish, MENU_MS);
      this.classList.remove('is-drawer-open');
      this.drawerToggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('hv2-locked');
      if (returnFocus) this.drawerToggle.focus();
    }

    openLevel(handle, opener) {
      const level = this.drawer.querySelector(`[data-hv2-level="${CSS.escape(handle)}"]`);
      if (!level) return;
      this.activeLevel = level;
      this.levelOpener = opener;
      clearTimeout(level.hv2HideTimer);
      level.hidden = false;
      level.scrollTop = 0;
      opener.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        level.classList.add('is-active');
        this.rootLevel.classList.add('is-behind');
      });
      level.querySelector('[data-hv2-back]')?.focus({ preventScroll: true });
    }

    closeLevel() {
      const level = this.activeLevel;
      if (!level) return;
      const opener = this.levelOpener;
      this.activeLevel = null;
      this.levelOpener = null;
      level.classList.remove('is-active');
      this.rootLevel.classList.remove('is-behind');
      opener?.setAttribute('aria-expanded', 'false');
      level.hv2HideTimer = setTimeout(() => {
        if (!level.classList.contains('is-active')) level.hidden = true;
      }, LEVEL_EXIT_MS);
      opener?.focus({ preventScroll: true });
    }

    resetLevels() {
      this.drawer.querySelectorAll('.hv2-level--child').forEach((level) => {
        level.classList.remove('is-active');
        level.hidden = true;
      });
      this.drawer
        .querySelectorAll('[data-hv2-open-level]')
        .forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.rootLevel?.classList.remove('is-behind');
      this.activeLevel = null;
      this.levelOpener = null;
    }

    trapFocus(event) {
      const scope = this.activeLevel || this.rootLevel;
      const focusables = [
        ...this.mobileBar.querySelectorAll(FOCUSABLE),
        ...scope.querySelectorAll(FOCUSABLE),
      ].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!focusables.includes(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Global handlers ---------- */

    onKeydown(event) {
      const drawerOpen = this.classList.contains('is-drawer-open');
      if (event.key === 'Escape') {
        if (drawerOpen) {
          if (this.activeLevel) this.closeLevel();
          else this.closeDrawer();
        } else if (this.openHandle) {
          this.closePanel({ returnFocus: true });
        }
      } else if (event.key === 'Tab' && drawerOpen) {
        this.trapFocus(event);
      }
    }

    onPageShow(event) {
      // bfcache restore after tapping a link inside the drawer/panel.
      if (event.persisted) this.closeAll(false);
    }

    closeAll(returnFocus) {
      clearTimeout(this.openTimer);
      this.cancelClose();
      this.closePanel({ immediate: true });
      this.closeDrawer(returnFocus, true);
      this.hideBackdrop(true);
    }

    /* ---------- Showroom live status ---------- */

    /*
      Each status element carries the showroom's hours rows as
      "Wednesday - Friday=12pm - 6pm|Monday - Tuesday=appt". Computed in the
      browser (Australia/Sydney time — Sydney and Melbourne share it) so the
      cached page never shows a stale status.
    */
    initShowroomStatus() {
      const items = this.querySelectorAll('[data-hv2-hours]');
      if (!items.length) return;

      const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayIndex = (text) => DAYS.indexOf(text.trim().slice(0, 3).toLowerCase());
      const parseDays = (label) => {
        const [from, to] = label.split(/\s+-\s+|\s*–\s*/);
        const start = dayIndex(from);
        if (start < 0) return [];
        const end = to ? dayIndex(to) : start;
        if (end < 0) return [start];
        const days = [];
        for (let d = start; ; d = (d + 1) % 7) {
          days.push(d);
          if (d === end) break;
        }
        return days;
      };
      const parseTime = (text) => {
        const m = text.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
        if (!m) return null;
        const hour = (Number(m[1]) % 12) + (m[3].toLowerCase() === 'pm' ? 12 : 0);
        return { minutes: hour * 60 + Number(m[2] || 0), label: text.trim().toLowerCase().replace(/\s+/g, '') };
      };

      let now;
      try {
        const parts = new Intl.DateTimeFormat('en-AU', {
          timeZone: 'Australia/Sydney',
          weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23',
        }).formatToParts(new Date());
        const get = (type) => parts.find((p) => p.type === type)?.value || '';
        now = { day: dayIndex(get('weekday')), minutes: Number(get('hour')) * 60 + Number(get('minute')) };
      } catch (error) {
        return;
      }
      if (now.day < 0) return;

      items.forEach((item) => {
        // week[d] = { appt: true } | { open, close } | undefined (closed)
        const week = [];
        item.dataset.hv2Hours.split('|').forEach((row) => {
          const [label, value = ''] = row.split('=');
          let entry;
          if (value === 'appt') {
            entry = { appt: true };
          } else {
            const [open, close] = value.split(/\s+-\s+|\s*–\s*/).map((t) => (t ? parseTime(t) : null));
            if (!open || !close) return;
            entry = { open, close };
          }
          parseDays(label).forEach((d) => {
            week[d] = entry;
          });
        });

        // Next day (from tomorrow) with walk-in hours.
        const nextOpen = () => {
          for (let i = 1; i <= 7; i++) {
            const d = (now.day + i) % 7;
            if (week[d]?.open) return { d, i, entry: week[d] };
          }
          return null;
        };

        const today = week[now.day];
        let main;
        let sub = '';
        let open = false;
        if (today?.appt) {
          main = 'By appointment today';
          const next = nextOpen();
          if (next) sub = `Walk-ins from ${SHORT[next.d]}`;
        } else if (today?.open && now.minutes >= today.open.minutes && now.minutes < today.close.minutes) {
          main = 'Open now';
          sub = `Closes ${today.close.label}`;
          open = true;
        } else if (today?.open && now.minutes < today.open.minutes) {
          main = 'Closed now';
          sub = `Opens ${today.open.label}`;
        } else {
          main = 'Closed now';
          const next = nextOpen();
          if (next) sub = `Opens ${next.i === 1 ? 'tomorrow' : SHORT[next.d]} ${next.entry.open.label}`;
        }

        item.querySelector('[data-hv2-status-main]').textContent = main;
        item.querySelector('[data-hv2-status-sub]').textContent = sub ? `· ${sub}` : '';
        item.classList.toggle('hv2-status--open', open);
        item.hidden = false;
      });
    }

    /* ---------- Transparent → filled (mobile) ---------- */

    initTransparent() {
      if (this.dataset.transparent !== 'true') return;
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const threshold = this.mobileBar?.offsetHeight || 48;
          this.classList.toggle('is-filled', window.scrollY > threshold);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    /* ---------- --header-height ---------- */

    initHeight() {
      const root = document.documentElement;
      const update = () => {
        root.style.setProperty('--header-height', `${this.offsetHeight}px`);
        if (this.mobileBar) root.style.setProperty('--hv2-bar-height', `${this.mobileBar.offsetHeight}px`);
      };
      update();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this);
      }
    }

    /* ---------- Utility strip placement ---------- */

    initUtilityStrip() {
      const section = this.closest('.shopify-section');
      const previous = section?.previousElementSibling;
      const hasBar = Boolean(previous?.querySelector('.announcement-bar'));
      this.classList.toggle('hv2--merged-strip', hasBar);
    }

    /* ---------- Cart count ---------- */

    initCartCount() {
      const source = this.querySelector('#cart-icon-bubble');
      const labels = this.querySelectorAll('[data-hv2-cart-count]');
      if (!source || !labels.length) return;

      const sync = () => {
        const match = source.textContent.match(/\d+/);
        const count = match ? match[0] : '0';
        labels.forEach((label) => {
          if (label.textContent !== count) label.textContent = count;
        });
      };

      this.cartObserver = new MutationObserver(sync);
      this.cartObserver.observe(source, { childList: true, subtree: true, characterData: true });
    }
  }

  customElements.define('header-v2', HeaderV2);
})();
